// Per-entry merge for Binder list items (collaborative family checklists).
//
// Shared-item sync is blind whole-item last-write-wins (the server is E2E-blind
// and cannot merge; see the Binder-lists spec §4-5). So two members editing the
// same list concurrently would clobber each other under a plain overwrite. This
// module reconciles `VaultItem.listItems` as a small STATE-BASED CRDT:
//
//   • entries union by `id` (an add is never lost),
//   • `done` is last-write-wins keyed by `doneAt` (latest toggle wins),
//   • removal is a TERMINAL tombstone (`deleted: true`) — a removed entry never
//     comes back, and a re-added item is a NEW `id`, so it coexists,
//   • `text` / `addedBy` / `addedAt` are write-once (identical for a given id).
//
// `mergeListEntries` is commutative, associative, and idempotent, so clients can
// run it on every pull, in any order, repeatedly, and always converge to the
// union. Clients merge an incoming list item with their local copy instead of
// replacing it, then push the merged superset back if it differs (spec §5).

import type { ListEntry, VaultItem } from './index';

/** Later ISO-8601 instant wins; a present instant beats an absent one; ties and
 *  two-absent fall back to `false` (keep `a`). Pure, total, deterministic. */
function isAfter(a: string | undefined, b: string | undefined): boolean {
  if (a === undefined) return false;
  if (b === undefined) return true;
  return a > b; // ISO-8601 instants compare lexicographically
}

/** Position (order) is an independent LWW field keyed on `positionedAt`, exactly
 *  like `done`/`doneAt`. Returns the position metadata of the winning side as a
 *  spreadable fragment (empty when neither side has a position). */
function positionFields(a: ListEntry, b: ListEntry): Partial<ListEntry> {
  const winner = isAfter(a.positionedAt, b.positionedAt) ? a : b;
  if (winner.position === undefined) return {};
  return {
    position: winner.position,
    ...(winner.positionedAt !== undefined ? { positionedAt: winner.positionedAt } : {}),
    ...(winner.positionedBy !== undefined ? { positionedBy: winner.positionedBy } : {}),
  };
}

/** Reconcile two versions of the same entry (same `id`). */
function mergeEntry(a: ListEntry, b: ListEntry): ListEntry {
  // Write-once identity fields: identical in practice; keep the earliest-added
  // copy defensively so a buggy client can't rewrite text/author.
  const base = isAfter(a.addedAt, b.addedAt) ? b : a;

  // Position is LWW independently of done/tombstone (a concurrent move + check of
  // the same item both land).
  const pos = positionFields(a, b);

  // Removal is terminal: if either side removed it, the merged entry is removed.
  if (a.deleted || b.deleted) {
    // Keep the removal metadata from the side with the later deletedAt.
    const remover = a.deleted && b.deleted
      ? (isAfter(a.deletedAt, b.deletedAt) ? a : b)
      : (a.deleted ? a : b);
    return {
      id: base.id,
      text: base.text,
      addedBy: base.addedBy,
      addedAt: base.addedAt,
      // Freeze the done state at removal time (whichever we carried); it is no
      // longer user-visible, but preserved for round-trip stability.
      done: remover.done,
      ...(remover.doneBy !== undefined ? { doneBy: remover.doneBy } : {}),
      ...(remover.doneAt !== undefined ? { doneAt: remover.doneAt } : {}),
      deleted: true,
      ...(remover.deletedBy !== undefined ? { deletedBy: remover.deletedBy } : {}),
      ...(remover.deletedAt !== undefined ? { deletedAt: remover.deletedAt } : {}),
      ...pos,
    };
  }

  // Live on both sides: done is LWW by doneAt.
  const winner = isAfter(a.doneAt, b.doneAt) ? a : b;
  return {
    id: base.id,
    text: base.text,
    addedBy: base.addedBy,
    addedAt: base.addedAt,
    done: winner.done,
    ...(winner.doneBy !== undefined ? { doneBy: winner.doneBy } : {}),
    ...(winner.doneAt !== undefined ? { doneAt: winner.doneAt } : {}),
    ...pos,
  };
}

/** Stable order: by `addedAt` ascending, then `id` as a deterministic tie-break
 *  (two entries can share an instant). UI may float done items to the bottom. */
function byAddedAtThenId(a: ListEntry, b: ListEntry): number {
  if (a.addedAt !== b.addedAt) return a.addedAt < b.addedAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Merge two entry arrays into their reconciled union. Commutative, associative,
 * and idempotent. Tombstones are retained (they must dominate a stale re-pull).
 */
export function mergeListEntries(
  a: readonly ListEntry[] = [],
  b: readonly ListEntry[] = [],
): ListEntry[] {
  const byId = new Map<string, ListEntry>();
  for (const e of a) byId.set(e.id, e);
  for (const e of b) {
    const existing = byId.get(e.id);
    byId.set(e.id, existing ? mergeEntry(existing, e) : e);
  }
  return [...byId.values()].sort(byAddedAtThenId);
}

/**
 * Merge an incoming list item with the local copy. For non-`list` items (or when
 * either side carries no `listItems`) this is a defensive no-op that returns
 * `incoming` unchanged — only `listItems` is reconciled; all other fields follow
 * the incoming (server) copy, consistent with whole-item sync for everything else.
 */
export function mergeListItem(local: VaultItem, incoming: VaultItem): VaultItem {
  if (incoming.type !== 'list' && local.type !== 'list') return incoming;
  const merged = mergeListEntries(local.listItems, incoming.listItems);
  return { ...incoming, listItems: merged };
}
